import { useEffect } from 'react';
import { useArtikelKatalog, useFavoriten } from '../hooks/data.js';
import { useFunktionen } from '../hooks/useFunktionen.js';

/** Bilder, die gleich nach dem Start im Hintergrund geholt werden (Favoriten, in Listen-Reihenfolge). */
const MAX_BILDER = 150;
const PARALLEL = 1; // Exxas erlaubt 2 Anfragen/s für alle zusammen – Bilder gehen hinten an
const schlafen = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * Gleich nach der Anmeldung im Hintergrund: Artikelstamm, Favoriten und deren Bilder laden, damit «Material»
 * sofort und auch ohne Netz alles zeigt. Die Bilder landen über den Service Worker dauerhaft auf dem Gerät –
 * schon vorhandene werden nicht erneut geholt.
 */
export function Vorladen() {
  return useFunktionen()('material') ? <MaterialVorladen /> : null; // nur Firmen mit Material
}

function MaterialVorladen() {
  useArtikelKatalog();
  const favoriten = useFavoriten();
  const urls = (favoriten.data?.artikel ?? []).map((a) => a.bildUrl).filter(Boolean).slice(0, MAX_BILDER);
  const schluessel = urls.join('|');

  useEffect(() => {
    // Browser bitten, den Speicher der App nicht automatisch zu räumen (Bilder, Offline-Daten)
    navigator.storage?.persist?.().catch(() => {});
  }, []);

  useEffect(() => {
    if (!urls.length || typeof caches === 'undefined' || !navigator.serviceWorker?.controller) return undefined;
    let abbruch = false;
    const timer = setTimeout(async () => {
      const cache = await caches.open('artikelbilder-v1').catch(() => null);
      if (!cache) return;
      const offen = [];
      for (const u of urls) {
        if (!(await cache.match(new URL(u, location.origin).href, { ignoreVary: true }))) offen.push(u);
      }
      let wartezeiten = 0;
      const arbeiter = Array.from({ length: PARALLEL }, async () => {
        while (offen.length && !abbruch && navigator.onLine !== false) {
          const url = offen.shift();
          const res = await fetch(url, { credentials: 'same-origin' }).catch(() => null);
          if (res?.status === 401) { abbruch = true; break; }
          if (res?.status === 429) {
            // Server lädt Bilder nur, wenn sonst nichts läuft → so lange warten, wie er sagt, dann dasselbe Bild nochmals
            if (++wartezeiten > 30) { abbruch = true; break; }
            offen.unshift(url);
            const sek = Number(res.headers.get('retry-after'));
            await schlafen(sek > 0 ? Math.min(sek * 1000 + 500, 70_000) : 10_000);
          }
        }
      });
      await Promise.all(arbeiter);
    }, 1500);
    return () => { abbruch = true; clearTimeout(timer); };
  }, [schluessel]); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
}
